<template>
  <div
    class="two-panel-split full-width no-wrap flex"
    :class="{'is-viewing': isViewing, 'mobile-mode': mobileMode}"
  >
    <div
      ref="listPane"
      class="list-pane full-height relative-position"
      :class="listClasses"
      :style="{'flex': `${$slots['view-empty'] ? 0 : 1} 0 ${currentListWidth}`}"
    >
      <slot
        v-if="withListToolbar"
        name="list-toolbar"
        :sticky-classes="stickyClasses"
      >
        <div
          :class="stickyClasses"
          style="z-index: 3;"
        >
          <q-toolbar>
            <q-btn
              v-if="withClose"
              flat
              dense
              round
              icon="close"
              @click="$emit('close')"
            />
            <page-toolbar-title v-if="title">
              {{ title }}
            </page-toolbar-title>
            <slot name="list-toolbar-inner" />
          </q-toolbar>
          <slot name="list-toolbar-after" />
        </div>
      </slot>
      <slot name="list" :small="mobileMode" />
    </div>
    <div
      v-if="$slots['view-empty'] || isViewing"
      class="view-pane full-height"
      :class="viewClasses"
    >
      <template v-if="isViewing">
        <q-toolbar
          :class="stickyClasses"
          style="z-index: 3;"
        >
          <q-btn
            v-if="mobileMode || withViewClose"
            flat
            round
            dense
            icon="mdi-arrow-left"
            @click="goBack"
          />
          <slot name="view-toolbar-after" :small="mobileMode">
            <q-toolbar-title v-if="viewTitle">
              {{ viewTitle }}
            </q-toolbar-title>
          </slot>
        </q-toolbar>
        <slot
          name="view"
          :back="goBack"
          :small="mobileMode"
        />
      </template>
      <div
        v-else
        class="flex flex-center full-height text-center q-pa-sm"
      >
        <slot name="view-empty" />
      </div>
    </div>
  </div>
</template>

<script>
import { stickyValidator, stickyClasses } from 'src/helpers/sticky'

/**
 * TwoPaneSplit is a dynamic two-column layout, typically with the left
 *  column being a list of items, and the right column displaying focusing on a single item
 *  from the list
 *
 *  EMITS:
 *  - close: close button on list
 *  - view-back: back button tapped inside single view
 *
 *  SLOTS:
 *  - list-toolbar: contents of the list toolbar (defaults to a simple toolbar)
 *  - list-toolbar-after: content to display below the default list toolbar
 *  - list-toolbar-inner: content to display on the right side of the main list toolbar
 *  - list: main content to display in the list column
 *  - view (back: func, small: boolean): main content to display on a focused list item
 *  - view-empty: default content to display in the view panel when not focused on an element.
 *        If slot is not supplied then the main list column will expand the full width available
 *  - view-toolbar-after: append content into view toolbar
 */
export default {
  props: {
    // Is view pane active / in focus
    isViewing: {
      type: Boolean,
      default: false
    },
    // title of the main list pane
    title: {
      type: String
    },
    // title to display in view panel
    viewTitle: {
      type: String
    },
    // show close button in main list pane
    withClose: {
      type: Boolean,
      default: false
    },
    // show toolbar on list pane
    withListToolbar: {
      type: Boolean,
      default: false
    },
    // show close button in view pane ABOVE breakpoint.
    // Will persist to display on mobile mode
    withViewClose: {
      type: Boolean,
      default: true
    },
    // classes to bind to the list column
    listClasses: {
      type: [String, Object]
    },
    // classes to bind to the view column
    viewClasses: {
      type: [String, Object]
    },
    // default width of list pane
    listWidth: {
      type: String,
      default: '65%'
    },
    // width of list pane when view pane is active
    listWidthWhenViewing: {
      type: String,
      default: '350px'
    },
    toolbarAnchor: {
      type: String,
      validator: stickyValidator,
      default: 'dialog'
    },
    // breakpoint for activating mobile mode
    breakpoint: {
      type: Number,
      default: 1024
    }
  },
  emits: ['close', 'viewBack'],
  computed: {
    stickyClasses () {
      return stickyClasses(this.toolbarAnchor, 'blurred-container no-border')
    },
    currentListWidth () {
      return this.isViewing ? this.listWidthWhenViewing : this.listWidth
    },
    mobileMode () {
      return this.$q.screen.width < this.breakpoint
    }
  },
  methods: {
    goBack () {
      this.$emit('viewBack')
    },
    scrollToTopList () {
      if (!this.$refs.listPane) {
        return
      }

      this.$refs.listPane.scrollTop = 0
    }
  }
}
</script>
